import { Form, Formik } from "formik";
import { ArrowRightIcon } from "vega-icons";
import * as Yup from "yup";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvestorInvestorInvitationComplete } from "@vega-api/generated/auth-invitation-investor-investor-v1/auth-invitation-investor-investor-v1";
import { useInvitationComplete } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { type InvitationCheckResponse } from "@vega-api/generated/types/invitationCheckResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { NameField } from "@vega-core/components/Form/Fields/NameField";
import {
  getE164PhoneValidationSchema,
  getNameValidation,
} from "@vega-core/helpers/formik.helper";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { useStatusPopup } from "@vega-core/hooks/useStatusPopup";
import { m } from "@vega-core/paraglide/messages";
import { formatFullName } from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { CountryField } from "@vega-modules/components/Form/Fields/CountryField";
import { PhoneField } from "@vega-modules/components/Form/Fields/PhoneField";

import { SuccessMessage } from "./components/SuccessMessage";

type Props = {
  invitationCode: string;
  invitationCheckData?: InvitationCheckResponse;
};

export const LPRegistrationForm = ({
  invitationCode,
  invitationCheckData,
}: Props) => {
  const { featureFlag } = useFeatureFlag();
  const { setError } = useStatusPopup();
  const {
    global: { companyName },
  } = usePublicConfig();

  const isInvitationV2 = featureFlag[FeatureFlag.GP_AUTH_INVITATION_V2];

  const completeMutationOptions = {
    mutation: {
      onSuccess: () => {
        sessionStorage.setItem("userRegistrationComplete", "true");
      },
      onError: (error: ErrorType<unknown>) => {
        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.errors_modalTitle(),
          defaultMessage: m.errors_unknown(),
        });
        setError({ title, description });
      },
    },
  };

  // Both hooks are called unconditionally (stable hook order); the flag only
  // selects which mutation result is used.
  const legacyComplete = useInvitationComplete(completeMutationOptions);
  const v2Complete = useInvestorInvestorInvitationComplete(
    completeMutationOptions,
  );

  const {
    data: invitationCompleteData,
    mutate: submitForm,
    isPending,
    isSuccess,
  } = isInvitationV2 ? v2Complete : legacyComplete;

  if (isSuccess) {
    return (
      <SuccessMessage
        invitationCheckData={invitationCheckData}
        invitationCompleteData={invitationCompleteData}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8 p-4 md:p-8 w-full">
      <div className="text-text/primary text-2xl font-semibold leading-normal">
        {m.registration_formTitle()}
      </div>
      <div className="text-text/secondary text-sm leading-relaxed">
        {isTruthy(invitationCheckData?.invitingUserFirstName) &&
          isTruthy(invitationCheckData.invitingUserLastName) &&
          m.registration_invitationMessage({
            name: formatFullName({
              firstName: invitationCheckData.invitingUserFirstName,
              lastName: invitationCheckData.invitingUserLastName,
            }),
            companyName: companyName,
          })}
      </div>
      <Formik
        initialValues={{
          name: { salutation: "", firstName: "", lastName: "" },
          position: "",
          countryCode: "",
          phone: "",
        }}
        onSubmit={({
          name: { salutation, firstName, lastName },
          position,
          countryCode,
          phone,
        }) => {
          const payload = {
            salutation,
            firstName,
            lastName,
            position,
            countryCode,
            phone,
            invitationCode,
          };

          submitForm({ data: payload });
        }}
        validationSchema={Yup.object({
          name: getNameValidation(),
          position: Yup.string(),
          countryCode: Yup.string().required(
            m.formValidation_required({
              name: m.formFields_country(),
            }),
          ),
          phone: getE164PhoneValidationSchema(),
        })}
      >
        {({ isValid }) => (
          <Form className="flex flex-col gap-6">
            <NameField honorificName="name.salutation" inline name="name" />
            <InputField
              label={m.formFields_titlePosition()}
              name="position"
              placeholder={m.formFields_specifyDepartment()}
            />
            <CountryField
              isRequired
              label={m.formFields_country()}
              name="countryCode"
            />
            <PhoneField name="phone" />
            <ThemedButton
              className="sm:w-1/2"
              dataTestId="next-button"
              disabled={!isValid}
              icon={<ArrowRightIcon />}
              iconPosition="right"
              isLoading={isPending}
              type="submit"
            >
              {m.btn_next()}
            </ThemedButton>
          </Form>
        )}
      </Formik>
    </div>
  );
};
